import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import styles from "./styles.module.scss";

// A client id is not a secret (it is in every checkout URL and token), but its random part makes a
// long table cell. Show the start and the end, keep the full id in the tooltip and behind the copy
// button.
const MAX_LENGTH = 30;
const COPIED_FEEDBACK_MS = 3000;

export const shortenClientId = (clientId: string) =>
  clientId.length <= MAX_LENGTH
    ? clientId
    : `${clientId.slice(0, 18)}…${clientId.slice(-8)}`;

const ClientIdLabel = ({
  clientId,
  highlighted = false,
}: {
  clientId: string;
  /** Marks the client an admin was sent to (a request link carries ?client=<id>). */
  highlighted?: boolean;
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const copy = async () => {
    try {
      await navigator?.clipboard?.writeText(clientId);
      toast.success(t("COPIED"));
      setCopied(true);
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(
        () => setCopied(false),
        COPIED_FEEDBACK_MS
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <span className={styles.container} style={{ direction: "ltr" }}>
      <span
        className={`${styles.value} ${highlighted ? styles.highlighted : ""}`}
        title={clientId}>
        {shortenClientId(clientId)}
      </span>
      <button
        type="button"
        className={`${styles.copy} ${copied ? styles.copied : ""}`}
        aria-label={t("lbl_copy")}
        title={t("lbl_copy")}
        onClick={copy}>
        {copied ? (
          <CheckIcon fontSize="inherit" />
        ) : (
          <ContentCopyIcon fontSize="inherit" />
        )}
      </button>
    </span>
  );
};

export default ClientIdLabel;
